import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Radio, X, ChevronRight, Play, Volume2, VolumeX, SkipForward } from 'lucide-react';
import { api } from '@/api';
import { getYouTubeEmbedUrl, formatDate, resolveMediaUrl } from '@/utils/helpers';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/common';

const POLL_INTERVAL_MS = 60_000;             // poll for new/ended games every 60s
const SPONSOR_BREAK_SECONDS = 15;            // length of the sponsor break between replays
const REPLAY_AUTOSTART_SECONDS = 3;          // brief intro before the channel kicks in
const REPLAY_MAX_DURATION_MS = 90 * 60_000;  // safety cap per replay (90 min — longer than any soccer match)

// Extract a YouTube video ID from any URL shape; returns null if not YouTube.
const extractYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const buildEmbedSrc = (url, muted) => {
  const base = getYouTubeEmbedUrl(url);
  if (!base) return null;
  const sep = base.includes('?') ? '&' : '?';
  // autoplay always on; controls/rel/modestbranding sane defaults; muted as requested
  return `${base}${sep}autoplay=1&mute=${muted ? 1 : 0}&rel=0&modestbranding=1`;
};

const labelFor = (game) => {
  if (!game) return '';
  const home = game.home_team_name || 'Home';
  const away = game.away_team_name || 'Away';
  return `${home} vs ${away}`;
};

/**
 * `/live` — auto-advancing live channel.
 *
 * UX:
 *  - Polls /api/games/live-now every 60s and diffs against the previous list.
 *  - When a new game appears, surfaces an inline "another game just started" banner
 *    with a "Switch now" / "Stay" action (auto-dismisses after 15s).
 *  - When the currently-playing game falls out of the live list, shows a "Game ended"
 *    banner and auto-advances to the next live game after a 5s countdown.
 *  - If there are no live games at all, shows a Recent Replays grid.
 */
export const LivePage = () => {
  const [liveGames, setLiveGames] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [muted, setMuted] = useState(true);
  const [loading, setLoading] = useState(true);
  const [replays, setReplays] = useState([]);
  const [newGameAlert, setNewGameAlert] = useState(null);   // {game}
  const [endedAlert, setEndedAlert] = useState(null);       // {endedTitle, nextGame, countdown}
  const lastIdsRef = useRef(new Set());
  const countdownTimerRef = useRef(null);
  // Stable refs to read the latest state from inside the fetchLive closure
  // without having to re-create the function (which would reset the 60s interval).
  const currentIdRef = useRef(null);
  const liveGamesRef = useRef([]);

  // ---- TV-style replay channel state (active when there are no live games) ----
  const [replayIndex, setReplayIndex] = useState(0);
  const [replayPhase, setReplayPhase] = useState('idle');    // 'idle' | 'intro' | 'playing' | 'sponsor'
  const [sponsorContent, setSponsorContent] = useState(null); // {name, logo, url}
  const [sponsorCountdown, setSponsorCountdown] = useState(SPONSOR_BREAK_SECONDS);
  const ytPlayerRef = useRef(null);
  const replayMountRef = useRef(null);          // div the YT IFrame API mounts into
  const replayMaxTimerRef = useRef(null);
  const sponsorTimerRef = useRef(null);
  const replayIndexRef = useRef(0);
  const replaysRef = useRef([]);
  useEffect(() => { replayIndexRef.current = replayIndex; }, [replayIndex]);
  useEffect(() => { replaysRef.current = replays; }, [replays]);

  useEffect(() => { currentIdRef.current = currentId; }, [currentId]);
  useEffect(() => { liveGamesRef.current = liveGames; }, [liveGames]);

  const startEndedFlow = useCallback((endedTitle, nextGame) => {
    // 5-second auto-advance countdown
    setEndedAlert({ endedTitle, nextGame, countdown: 5 });
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    countdownTimerRef.current = setInterval(() => {
      setEndedAlert(prev => {
        if (!prev) return prev;
        if (prev.countdown <= 1) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          if (prev.nextGame) {
            setTimeout(() => setCurrentId(prev.nextGame.id), 0);
          } else {
            setTimeout(() => setCurrentId(null), 0);
          }
          return null;
        }
        return { ...prev, countdown: prev.countdown - 1 };
      });
    }, 1_000);
  }, []);

  // -- Fetcher (shared by initial load + interval poll). STABLE — no deps. --
  const fetchLive = useCallback(async () => {
    try {
      const res = await api.getLiveNowGames();
      const games = res.data || [];
      const newIds = new Set(games.map(g => g.id));
      const prevIds = lastIdsRef.current;
      const cur = currentIdRef.current;
      const prevGames = liveGamesRef.current;

      // Detect newly-appeared games — but skip on first load (prevIds is empty)
      if (prevIds.size > 0) {
        const appeared = games.filter(g => !prevIds.has(g.id));
        if (appeared.length > 0) {
          const incoming = appeared[0];
          if (incoming.id !== cur) {
            setNewGameAlert({ game: incoming });
            setTimeout(
              () => setNewGameAlert(prev => (prev?.game?.id === incoming.id ? null : prev)),
              15_000,
            );
          }
        }
      }

      // If the currently-watched game just left the live list, fire ended flow.
      if (cur && prevIds.has(cur) && !newIds.has(cur)) {
        const ended = prevGames.find(g => g.id === cur);
        const endedTitle = ended ? labelFor(ended) : 'Previous game';
        const nextGame = games[0] || null;
        startEndedFlow(endedTitle, nextGame);
      }

      lastIdsRef.current = newIds;
      setLiveGames(games);

      // Auto-select first live game when nothing is selected and games exist
      if (!cur && games.length > 0) {
        setCurrentId(games[0].id);
      }
    } catch (err) {
      console.error('Live polling failed', err);
    } finally {
      setLoading(false);
    }
  }, [startEndedFlow]);

  // -- Initial load: live games + recent replays in parallel --
  useEffect(() => {
    Promise.all([
      api.getLiveNowGames().then(r => r.data || []),
      api.getRecentReplays(12).then(r => r.data || []),
    ]).then(([games, reps]) => {
      lastIdsRef.current = new Set(games.map(g => g.id));
      setLiveGames(games);
      setReplays(reps);
      if (games.length > 0) setCurrentId(games[0].id);
      setLoading(false);
    }).catch(err => {
      console.error('Initial live page load failed', err);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- Polling interval --
  useEffect(() => {
    const id = setInterval(fetchLive, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchLive]);

  // -- Refresh replays whenever there are no live games --
  useEffect(() => {
    if (!loading && liveGames.length === 0) {
      api.getRecentReplays(12).then(r => setReplays(r.data || [])).catch(() => {});
    }
  }, [loading, liveGames.length]);

  // Cleanup
  useEffect(() => () => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (replayMaxTimerRef.current) clearTimeout(replayMaxTimerRef.current);
    if (sponsorTimerRef.current) clearInterval(sponsorTimerRef.current);
    if (ytPlayerRef.current && ytPlayerRef.current.destroy) {
      try { ytPlayerRef.current.destroy(); }
      catch (err) { console.warn('LivePage: YT.Player.destroy on unmount failed (harmless):', err); }
      ytPlayerRef.current = null;
    }
  }, []);

  // ============== TV-STYLE REPLAY CHANNEL ==============
  // Active when: no live games AND replays are available. Plays each replay back-to-back,
  // showing a 15s sponsor break (manual_sponsors / paid campaigns from /ads/serve/video_player)
  // between replays. Uses the YouTube IFrame Player API to detect when a video ends.

  const loadYouTubeIframeAPI = () => new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    if (window.__wpslYtApiLoading) {
      const check = setInterval(() => {
        if (window.YT && window.YT.Player) { clearInterval(check); resolve(window.YT); }
      }, 100);
      return;
    }
    window.__wpslYtApiLoading = true;
    const existingCb = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (existingCb) existingCb();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });

  const goToSponsorBreak = useCallback(async () => {
    // Tear down the player so the sponsor card takes over the slot
    if (ytPlayerRef.current && ytPlayerRef.current.destroy) {
      try { ytPlayerRef.current.destroy(); }
      catch (err) { console.warn('LivePage: YT.Player.destroy on sponsor-break failed (harmless):', err); }
      ytPlayerRef.current = null;
    }
    if (replayMaxTimerRef.current) {
      clearTimeout(replayMaxTimerRef.current);
      replayMaxTimerRef.current = null;
    }
    // Fetch a sponsor (paid campaign first, then manual_sponsors)
    let sponsor = null;
    try {
      const res = await api.getVideoPlayerAd();
      const d = res.data || {};
      if (d.type === 'sponsors' && Array.isArray(d.sponsors) && d.sponsors.length > 0) {
        const s = d.sponsors[Math.floor(Math.random() * d.sponsors.length)];
        sponsor = { name: s.company_name, logo: s.logo_url, url: s.click_url };
      } else if (d.type === 'ad' && d.ad) {
        sponsor = {
          name: 'Sponsored',
          logo: d.ad.image_url,
          url: d.ad.click_url,
        };
      }
    } catch (err) {
      // Intentional fallback: any failure (4xx/5xx/network) falls through to the
      // "Become a WPSL Partner" CTA below so the channel never stalls.
      console.warn('LivePage: sponsor ad fetch failed, using fallback card:', err);
    }
    if (!sponsor) {
      sponsor = {
        name: 'Become a WPSL Partner',
        logo: null,
        url: '/partners',
        fallback: true,
      };
    }
    setSponsorContent(sponsor);
    setSponsorCountdown(SPONSOR_BREAK_SECONDS);
    setReplayPhase('sponsor');
    // Countdown
    if (sponsorTimerRef.current) clearInterval(sponsorTimerRef.current);
    sponsorTimerRef.current = setInterval(() => {
      setSponsorCountdown(c => {
        if (c <= 1) {
          clearInterval(sponsorTimerRef.current);
          sponsorTimerRef.current = null;
          // Advance to next replay (cycle)
          setReplayIndex(prev => {
            const list = replaysRef.current;
            if (list.length === 0) return prev;
            return (prev + 1) % list.length;
          });
          setReplayPhase('playing');
          return 0;
        }
        return c - 1;
      });
    }, 1_000);
  }, []);

  const playReplayAtIndex = useCallback(async (idx) => {
    const list = replaysRef.current;
    const replay = list[idx];
    if (!replay) return;
    const vid = extractYouTubeId(replay.stream_url);
    if (!vid) return;
    const YT = await loadYouTubeIframeAPI();
    if (!replayMountRef.current) return;
    // Destroy any previous player
    if (ytPlayerRef.current && ytPlayerRef.current.destroy) {
      try { ytPlayerRef.current.destroy(); }
      catch (err) { console.warn('LivePage: YT.Player.destroy before remount failed (harmless):', err); }
      ytPlayerRef.current = null;
    }
    // YT.Player replaces the target node with its own iframe; recreate a fresh
    // child node so we can create a new player without leaving an orphaned iframe behind.
    // Using DOM APIs (not innerHTML) — keeps the lint clean and avoids any chance of
    // unintended HTML parsing if this code is ever refactored to accept dynamic ids.
    while (replayMountRef.current.firstChild) {
      replayMountRef.current.removeChild(replayMountRef.current.firstChild);
    }
    const target = document.createElement('div');
    target.id = 'wpsl-replay-yt-target';
    replayMountRef.current.appendChild(target);
    ytPlayerRef.current = new YT.Player(target, {
      width: '100%',
      height: '100%',
      videoId: vid,
      playerVars: { autoplay: 1, mute: 1, rel: 0, modestbranding: 1, playsinline: 1 },
      events: {
        onStateChange: (e) => {
          // 0 = ENDED — go to sponsor break, then next replay
          if (e.data === 0) goToSponsorBreak();
        },
      },
    });
    // Safety cap so we never get stuck if a video stalls or the ENDED event never fires
    if (replayMaxTimerRef.current) clearTimeout(replayMaxTimerRef.current);
    replayMaxTimerRef.current = setTimeout(() => goToSponsorBreak(), REPLAY_MAX_DURATION_MS);
  }, [goToSponsorBreak]);

  // Kick off replay channel when conditions are met.
  // NOTE: replayPhase is intentionally NOT in deps — when this effect updates
  // replayPhase from 'idle' -> 'intro', a re-run would clear the pending setTimeout
  // via the cleanup function and the channel would never reach 'playing'.
  const replayPhaseRef = useRef('idle');
  useEffect(() => { replayPhaseRef.current = replayPhase; }, [replayPhase]);
  useEffect(() => {
    if (loading || liveGames.length > 0 || replays.length === 0) return;
    if (replayPhaseRef.current !== 'idle') return;
    setReplayPhase('intro');
    const t = setTimeout(() => setReplayPhase('playing'), REPLAY_AUTOSTART_SECONDS * 1000);
    return () => clearTimeout(t);
  }, [loading, liveGames.length, replays.length]);

  // When replayPhase becomes 'playing' OR replayIndex changes, mount the next video
  useEffect(() => {
    if (replayPhase !== 'playing') return;
    playReplayAtIndex(replayIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayPhase, replayIndex]);

  // When a live game appears mid-replay-mode, tear down replay channel cleanly
  useEffect(() => {
    if (liveGames.length === 0) return;
    if (replayPhase === 'idle') return;
    if (ytPlayerRef.current && ytPlayerRef.current.destroy) {
      try { ytPlayerRef.current.destroy(); }
      catch (err) { console.warn('LivePage: YT.Player.destroy on live-takeover failed (harmless):', err); }
      ytPlayerRef.current = null;
    }
    if (replayMaxTimerRef.current) { clearTimeout(replayMaxTimerRef.current); replayMaxTimerRef.current = null; }
    if (sponsorTimerRef.current) { clearInterval(sponsorTimerRef.current); sponsorTimerRef.current = null; }
    setReplayPhase('idle');
    setSponsorContent(null);
  }, [liveGames.length, replayPhase]);

  const skipToNextReplay = () => goToSponsorBreak();

  const currentReplay = replays[replayIndex] || null;

  const currentGame = liveGames.find(g => g.id === currentId) || null;
  const otherLive = liveGames.filter(g => g.id !== currentId);

  const switchTo = (gameId) => {
    setCurrentId(gameId);
    setNewGameAlert(null);
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setEndedAlert(null);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="live-page">
      <SEO
        title="Live Now — WPSL TV"
        description="Watch every WPSL match that's live right now. Auto-advances as games end."
      />

      {/* Header */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Radio className={`w-7 h-7 ${liveGames.length > 0 ? 'text-red-500 animate-pulse' : 'text-gray-500'}`} />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Live Now
              </h1>
              <p className="text-sm text-gray-400" data-testid="live-count">
                {loading ? 'Loading…' : (liveGames.length > 0
                  ? `${liveGames.length} game${liveGames.length === 1 ? '' : 's'} live`
                  : 'No games live right now — showing recent replays')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="py-24 text-center text-gray-400" data-testid="live-loading">Tuning in…</div>
        ) : currentGame ? (
          <>
            {/* Player */}
            <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-gray-800" data-testid="live-player">
              <div className="aspect-video w-full">
                <iframe
                  key={`${currentGame.id}-${muted ? 'm' : 'u'}`}
                  src={buildEmbedSrc(currentGame.stream_url, muted)}
                  title={labelFor(currentGame)}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  data-testid="live-iframe"
                />
              </div>

              {/* Floating mute toggle */}
              <button
                onClick={() => setMuted(m => !m)}
                className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm border border-white/20 rounded-full p-2 hover:bg-black/90 transition"
                data-testid="live-mute-toggle"
                aria-label={muted ? 'Unmute' : 'Mute'}
                title={muted ? 'Unmute' : 'Mute'}
              >
                {muted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
              </button>
            </div>

            {/* Current game meta */}
            <div className="mt-4 flex items-start justify-between gap-4 flex-wrap" data-testid="live-meta">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center gap-1 bg-red-600 text-white text-xs uppercase tracking-wider font-bold px-2 py-0.5 rounded">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                  </span>
                  {currentGame.league_name && (
                    <span className="text-xs uppercase tracking-wider text-gray-400">{currentGame.league_name}</span>
                  )}
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {labelFor(currentGame)}
                </h2>
                <p className="text-xs text-gray-500 mt-1">Started {formatDate(currentGame.scheduled_time)}</p>
              </div>
              <Link
                to={`/watch/${currentGame.id}`}
                className="text-sm text-sky-400 hover:text-sky-300 inline-flex items-center gap-1"
                data-testid="live-open-watch"
              >
                Open in theater <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Other live games strip */}
            {otherLive.length > 0 && (
              <div className="mt-8" data-testid="live-strip">
                <h3 className="text-xs uppercase tracking-widest text-gray-400 mb-3 font-bold">
                  Also live now
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {otherLive.map(g => (
                    <button
                      key={g.id}
                      onClick={() => switchTo(g.id)}
                      className="group relative bg-gray-900 hover:bg-gray-800 border border-gray-800 hover:border-red-500/50 rounded-lg p-3 text-left transition"
                      data-testid={`live-strip-card-${g.id}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded">
                          <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> LIVE
                        </span>
                      </div>
                      <p className="text-sm font-medium text-white line-clamp-2">{labelFor(g)}</p>
                      {g.league_name && (
                        <p className="text-[11px] text-gray-500 mt-1 uppercase tracking-wider">{g.league_name}</p>
                      )}
                      <Play className="absolute bottom-3 right-3 w-4 h-4 text-gray-600 group-hover:text-red-500" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          // ---- No live games → TV-style replay channel ----
          <div data-testid="live-empty-state">
            {replays.length === 0 ? (
              <>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
                  <Radio className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <h2 className="text-xl font-bold text-white mb-1">Nothing's live right now</h2>
                  <p className="text-sm text-gray-400">We don't have any recent replays to play either. Check back on game day!</p>
                </div>
                <p className="text-center text-gray-500 mt-4" data-testid="live-no-replays">No recent replays available.</p>
              </>
            ) : (
              <>
                {/* Intro card — shown briefly while channel starts up */}
                {replayPhase === 'intro' && (
                  <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center mb-4" data-testid="live-replay-intro">
                    <Radio className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <h2 className="text-xl font-bold text-white mb-1">Nothing's live right now</h2>
                    <p className="text-sm text-gray-400">Starting the replay channel — sit back and enjoy recent matches with sponsor breaks between games.</p>
                  </div>
                )}

                {/* The TV slot — either the YT replay player or the sponsor break overlay */}
                <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-gray-800" data-testid="live-replay-slot">
                  <div className="aspect-video w-full">
                    {replayPhase === 'sponsor' && sponsorContent ? (
                      // Sponsor break card
                      <a
                        href={sponsorContent.url || '#'}
                        target={sponsorContent.url && sponsorContent.url.startsWith('http') ? '_blank' : undefined}
                        rel="noopener noreferrer"
                        className="block w-full h-full bg-gradient-to-br from-gray-900 via-gray-800 to-black flex flex-col items-center justify-center text-center px-8 hover:from-gray-800 transition"
                        data-testid="live-sponsor-break"
                      >
                        <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold mb-4">A short word from</span>
                        {sponsorContent.logo ? (
                          <img
                            src={resolveMediaUrl(sponsorContent.logo)}
                            alt={sponsorContent.name}
                            className="max-h-32 max-w-md object-contain mb-6"
                          />
                        ) : null}
                        <h3 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          {sponsorContent.name}
                        </h3>
                        {sponsorContent.fallback && (
                          <p className="text-sm text-gray-400 max-w-md">
                            Your brand could be here. Reach soccer fans across the country during the WPSL replay channel.
                          </p>
                        )}
                        <div className="absolute bottom-4 right-4 text-xs text-gray-500 font-mono bg-black/60 px-2 py-1 rounded" data-testid="live-sponsor-countdown">
                          Next replay in {sponsorCountdown}s
                        </div>
                      </a>
                    ) : replayPhase === 'playing' ? (
                      // YouTube IFrame Player API mount point — CSS ensures the
                      // generated <iframe> fills the slot even if the API decides
                      // to inject default 640x390 dimensions.
                      <div
                        ref={replayMountRef}
                        className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:absolute [&>iframe]:inset-0"
                        data-testid="live-replay-player"
                      />
                    ) : (
                      // Intro idle state — show a still poster
                      <div className="w-full h-full flex items-center justify-center bg-gray-900">
                        <Radio className="w-16 h-16 text-gray-700 animate-pulse" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Now-playing strip */}
                <div className="mt-4 flex items-start justify-between gap-4 flex-wrap" data-testid="live-replay-meta">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center gap-1 bg-gray-700 text-gray-100 text-xs uppercase tracking-wider font-bold px-2 py-0.5 rounded">
                        {replayPhase === 'sponsor' ? 'Sponsor Break' : 'Replay Channel'}
                      </span>
                      {currentReplay?.league_name && replayPhase !== 'sponsor' && (
                        <span className="text-xs uppercase tracking-wider text-gray-400">{currentReplay.league_name}</span>
                      )}
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {replayPhase === 'sponsor'
                        ? "We'll be right back…"
                        : currentReplay ? `Now playing: ${labelFor(currentReplay)}` : 'Loading replay channel…'}
                    </h2>
                    {currentReplay && replayPhase !== 'sponsor' && (
                      <p className="text-xs text-gray-500 mt-1">{formatDate(currentReplay.scheduled_time)}</p>
                    )}
                  </div>
                  {replayPhase === 'playing' && replays.length > 1 && (
                    <Button
                      onClick={skipToNextReplay}
                      variant="outline"
                      className="border-gray-600 text-gray-300 hover:bg-gray-800"
                      data-testid="live-replay-skip"
                    >
                      <SkipForward className="w-4 h-4 mr-1" />
                      Skip to next
                    </Button>
                  )}
                </div>

                {/* Up-next strip */}
                {replays.length > 1 && (
                  <div className="mt-8" data-testid="live-replay-upnext-strip">
                    <h3 className="text-xs uppercase tracking-widest text-gray-400 mb-3 font-bold">Up next</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {replays.map((g, i) => {
                        const isCurrent = i === replayIndex;
                        return (
                          <button
                            key={g.id}
                            onClick={() => { setReplayIndex(i); setReplayPhase('playing'); }}
                            className={`group relative bg-gray-900 hover:bg-gray-800 border rounded-lg p-3 text-left transition ${isCurrent ? 'border-sky-500/60 ring-1 ring-sky-500/40' : 'border-gray-800 hover:border-sky-500/40'}`}
                            data-testid={`live-replay-card-${g.id}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${isCurrent ? 'bg-sky-600 text-white' : 'bg-gray-800 text-gray-300'}`}>
                                {isCurrent ? 'Playing' : 'Replay'}
                              </span>
                            </div>
                            <p className="text-sm font-medium text-white line-clamp-2">{labelFor(g)}</p>
                            {g.league_name && (
                              <p className="text-[11px] text-gray-500 mt-1 uppercase tracking-wider">{g.league_name}</p>
                            )}
                            <Play className={`absolute bottom-3 right-3 w-4 h-4 ${isCurrent ? 'text-sky-400' : 'text-gray-600 group-hover:text-sky-400'}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ---- New-game-started banner ---- */}
      {newGameAlert && newGameAlert.game.id !== currentId && (
        <div
          className="fixed bottom-6 right-6 max-w-sm bg-gray-900 border border-red-500/50 rounded-xl shadow-2xl p-4 z-50 animate-in slide-in-from-right"
          data-testid="live-new-game-alert"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded shrink-0 mt-0.5">
              <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> NEW
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">Another game just started</p>
              <p className="text-sm font-bold text-white mt-0.5 truncate">{labelFor(newGameAlert.game)}</p>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs"
                  onClick={() => switchTo(newGameAlert.game.id)}
                  data-testid="live-new-game-switch"
                >
                  Switch now
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-gray-400 hover:text-white h-8 text-xs"
                  onClick={() => setNewGameAlert(null)}
                  data-testid="live-new-game-dismiss"
                >
                  Stay
                </Button>
              </div>
            </div>
            <button
              onClick={() => setNewGameAlert(null)}
              className="text-gray-500 hover:text-white shrink-0"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ---- Game ended + auto-advance banner ---- */}
      {endedAlert && (
        <div
          className="fixed bottom-6 right-6 max-w-sm bg-gray-900 border border-sky-500/50 rounded-xl shadow-2xl p-4 z-50"
          data-testid="live-ended-alert"
        >
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">Game ended</p>
              <p className="text-sm font-bold text-white mt-0.5 truncate">{endedAlert.endedTitle}</p>
              {endedAlert.nextGame ? (
                <>
                  <p className="text-xs text-gray-400 mt-2">
                    Switching to <span className="text-white font-medium">{labelFor(endedAlert.nextGame)}</span> in {endedAlert.countdown}s…
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      className="bg-sky-600 hover:bg-sky-700 text-white h-8 text-xs"
                      onClick={() => switchTo(endedAlert.nextGame.id)}
                      data-testid="live-ended-switch-now"
                    >
                      Switch now
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-400 hover:text-white h-8 text-xs"
                      onClick={() => {
                        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
                        setEndedAlert(null);
                      }}
                      data-testid="live-ended-cancel"
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-gray-400 mt-2">No other games live. Browse the recent replays below.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LivePage;
